import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Iconify } from 'src/components';

const SPEAKER_OPTIONS = [
  {
    value: 'yes_speaking',
    title: 'Yes, speaking',
    subtitle: 'Correct person',
    icon: 'solar:check-circle-bold',
    color: '#22C55E',
    bgColor: '#22C55E14',
  },
  {
    value: 'no_answer',
    title: 'No answer',
    subtitle: 'Voicemail',
    icon: 'solar:phone-calling-rounded-bold',
    color: '#FFAB00',
    bgColor: '#FFAB0014',
  },
  {
    value: 'wrong_person',
    title: 'Wrong person',
    subtitle: '',
    icon: 'solar:user-cross-bold',
    color: '#FF5630',
    bgColor: '#FF563014',
  },
];

const ConfirmSpeakerLive = ({ formData, onChange, disabled, onSelect }) => {
  const theme = useTheme();
  const selectedValue = formData.confirmSpeaker || '';

  const handleSelect = useCallback((value) => {
    if (disabled) return;
    const newValue = selectedValue === value ? '' : value;
    onChange('confirmSpeaker', newValue);
    // Notify parent of selection (only when selecting, not deselecting)
    if (newValue && onSelect) {
      onSelect(newValue);
    }
  }, [selectedValue, onChange, disabled, onSelect]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Response options */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {SPEAKER_OPTIONS.map((option) => {
          const isSelected = selectedValue === option.value;
          return (
            <Box
              key={option.value}
              onClick={() => handleSelect(option.value)}
              sx={{
                flex: '1 1 auto',
                minWidth: 140,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: '12px',
                border: `1.5px solid ${isSelected ? option.color : theme.palette.divider}`,
                backgroundColor: isSelected ? option.bgColor : theme.palette.background.paper,
                cursor: disabled ? 'default' : 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': disabled ? {} : {
                  borderColor: option.color,
                  backgroundColor: option.bgColor,
                },
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: option.bgColor,
                  flexShrink: 0,
                }}
              >
                <Iconify
                  icon={option.icon}
                  sx={{
                    width: 20,
                    height: 20,
                    color: option.color,
                  }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? option.color : theme.palette.text.primary,
                    lineHeight: 1.3,
                  }}
                >
                  {option.title}
                </Typography>
                {option.subtitle && (
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      color: theme.palette.text.secondary,
                      lineHeight: 1.3,
                    }}
                  >
                    {option.subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

ConfirmSpeakerLive.propTypes = {
  formData: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func,
};

ConfirmSpeakerLive.defaultProps = {
  formData: {},
  onChange: () => {},
  disabled: false,
  onSelect: null,
};

export default ConfirmSpeakerLive;
